import React from 'react';
import { Card, Suit } from '../../state/ArenaTypes';
import { getSuitSymbol, getSuitColor } from '../../engine/StandardDeck';
import { useArenaStore } from '../../store/useArenaStore';
import { HeartsQueenOfSpadesPortrait } from '../../games/Hearts/presentation/HeartsQueenOfSpadesPortrait';
import { motion } from 'framer-motion';
import { SouthHandFontSize, useSouthHandFontSize, useCardHints } from '../Table/hooks/useArenaTableSettings';

export interface CardViewProps {
  card: Card;
  isPlayable?: boolean;
  isSelected?: boolean;
  isFaceDown?: boolean;
  onClick?: () => void;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isGold?: boolean;
  hasShadow?: boolean;
  /** Explicitly enables or disables Hearts Queen of Spades art */
  isHearts?: boolean;
  /** Active card game type if known, e.g. 'HEARTS', 'BID_WHIST', 'SPADES' */
  gameType?: string;
  /** Increases typography slightly for South player hand for enhanced readability */
  isSouthHand?: boolean;
  /** Font size for South player hand: 'regular' | 'medium' | 'extra-large' */
  southHandFontSize?: SouthHandFontSize;
  /** Explicitly toggle playability hint dimming (defaults to useCardHints() setting) */
  showHints?: boolean;
}

export interface CardBackProps {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

export const CardBack: React.FC<CardBackProps> = React.memo(({
  size = 'md',
  className = ''
}) => {
  const sizeClasses = {
    xs: 'w-[36px] h-[52px] text-[8px] rounded-md',
    sm: 'w-[80px] h-[116px] sm:w-[86px] sm:h-[124px] text-xs rounded-xl',
    md: 'w-[85px] h-[122px] sm:w-[94px] sm:h-[135px] md:w-[103px] md:h-[148px] rounded-2xl',
    lg: 'w-26 h-36 sm:w-30 sm:h-42 text-sm sm:text-base rounded-2xl'
  }[size];

  const textSizes = {
    xs: {
      fin: 'w-3 h-3',
      main: 'text-[5px] tracking-[0.1em]',
      sub: 'text-[4px] tracking-[0.1em]'
    },
    sm: {
      fin: 'w-6 h-6 sm:w-6.5 sm:h-6.5',
      main: 'text-[9px] sm:text-[10px] tracking-[0.12em]',
      sub: 'text-[8px] sm:text-[9px] tracking-[0.11em]'
    },
    md: {
      fin: 'w-6.5 h-6.5 sm:w-7 sm:h-7',
      main: 'text-[10px] sm:text-[11px] tracking-[0.14em]',
      sub: 'text-[9px] sm:text-[10px] tracking-[0.12em]'
    },
    lg: {
      fin: 'w-8 h-8 sm:w-10 sm:h-10',
      main: 'text-[12px] sm:text-[14px] tracking-[0.16em]',
      sub: 'text-[11px] sm:text-[13px] tracking-[0.14em]'
    }
  }[size];

  return (
    <div
      className={`${sizeClasses} card-back card-crisp bg-white p-1 sm:p-1.5 shadow-[0_6px_20px_rgba(0,0,0,0.8)] border border-slate-300 ring-1 ring-slate-400/20 flex items-center justify-center cursor-default select-none relative overflow-hidden ${className}`}
    >
      {/* Inner Dark Navy/Charcoal Felt Panel with Subtle Diamond Lattice */}
      <div className="w-full h-full rounded-lg sm:rounded-xl relative flex flex-col items-center justify-center bg-gradient-to-b from-[#0a1526] via-[#050c18] to-[#0a1526] border border-cyan-500/40 overflow-hidden shadow-inner p-2">
        {/* Crisp Vector Diamond Grid Pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-20 pointer-events-none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="cardBackDiamondPattern" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 0 5 L 5 0 L 10 5 L 5 10 Z" fill="none" stroke="#38bdf8" strokeWidth="0.6" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#cardBackDiamondPattern)" />
        </svg>

        {/* Crisp Inner Inset Border */}
        <div className="absolute inset-1 border border-cyan-400/20 rounded-md pointer-events-none" />

        {/* Refined Center Emblem */}
        <div className="relative z-10 flex flex-col items-center justify-center">
          <svg viewBox="0 0 32 32" className={`${textSizes.fin} drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]`}>
            <defs>
              <linearGradient id="cardBackFinGradRefined" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#7dd3fc" />
                <stop offset="100%" stopColor="#0284c7" />
              </linearGradient>
            </defs>
            <path
              d="M 6,24 C 10,23 13,18 16,12 C 18,8 20,4 21,3 C 21,7 23,14 26,20 C 27,22 25,24 23,24 Z"
              fill="url(#cardBackFinGradRefined)"
            />
          </svg>
          <div className="flex flex-col items-center justify-center text-center mt-1 leading-[1.1] select-none pointer-events-none">
            <span className={`${textSizes.main} font-mono font-black uppercase text-cyan-200/95 drop-shadow-sm`}>
              ATL
            </span>
            <span className={`${textSizes.sub} font-mono font-black uppercase text-cyan-300/85 drop-shadow-sm`}>
              CARD
            </span>
            <span className={`${textSizes.main} font-mono font-black uppercase text-cyan-200/95 drop-shadow-sm`}>
              SHARKS
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

let currentIsBlackClubs: boolean = true;
if (typeof localStorage !== 'undefined') {
  try {
    currentIsBlackClubs = localStorage.getItem('atl_club_color') !== 'emerald';
  } catch {}
}

const clubColorListeners = new Set<(isBlack: boolean) => void>();

function notifyClubColorChange() {
  try {
    const saved = localStorage.getItem('atl_club_color');
    currentIsBlackClubs = saved !== 'emerald';
  } catch {}
  clubColorListeners.forEach(l => l(currentIsBlackClubs));
}

let isGlobalClubColorListening = false;
function ensureClubColorListener() {
  if (isGlobalClubColorListening || typeof window === 'undefined') return;
  isGlobalClubColorListening = true;
  window.addEventListener('atl_club_color_change', notifyClubColorChange);
  window.addEventListener('storage', notifyClubColorChange);
}

export function useClubColor(): boolean {
  const [isBlackClubs, setIsBlackClubs] = React.useState<boolean>(currentIsBlackClubs);

  React.useEffect(() => {
    ensureClubColorListener();
    clubColorListeners.add(setIsBlackClubs);
    return () => {
      clubColorListeners.delete(setIsBlackClubs);
    };
  }, []);

  return isBlackClubs;
}

export type { HeartsQueenOfSpadesPortraitProps as QueenOfSpadesPortraitProps } from '../../games/Hearts/presentation/HeartsQueenOfSpadesPortrait';
export const QueenOfSpadesPortrait = HeartsQueenOfSpadesPortrait;

// Typography for 'sm' and 'lg' cards is driven strictly by the card's own size, never by viewport
// breakpoints: the md: breakpoint (iPad and wider) used to blow glyphs up to sizes tuned for the
// 103px 'md' card, making ornaments overflow smaller cards.
const SM_TYPO = {
  rank: { regular: 'text-[22px] sm:text-[24.2px]', medium: 'text-[23.1px] sm:text-[25.3px]', 'extra-large': 'text-[24.2px] sm:text-[26.4px]' },
  suit: { regular: 'text-[17.6px] sm:text-[19.8px]', medium: 'text-[18.7px] sm:text-[20.9px]', 'extra-large': 'text-[19.8px] sm:text-[22px]' },
  star: { regular: 'text-[19.8px] sm:text-[22px]', medium: 'text-[20.9px] sm:text-[23.1px]', 'extra-large': 'text-[22px] sm:text-[24.2px]' },
  center: { regular: 'text-[50.6px] sm:text-[57.2px]', medium: 'text-[52.8px] sm:text-[59.4px]', 'extra-large': 'text-[55px] sm:text-[61.6px]' },
} as const;

const LG_TYPO = {
  rank: 'text-3xl sm:text-4xl',
  suit: 'text-2xl sm:text-3xl',
  star: 'text-3xl sm:text-4xl',
  center: 'text-7xl sm:text-8xl',
} as const;

const getRankClass = (size: string, isSouthHand: boolean, fontSize: SouthHandFontSize) => {
  if (size === 'xs') return 'text-[10px] font-black card-rank-text';
  if (size === 'sm') return `${SM_TYPO.rank[fontSize] ?? SM_TYPO.rank.regular} font-black card-rank-text`;
  if (size === 'lg') return `${LG_TYPO.rank} font-black card-rank-text`;
  if (!isSouthHand) return 'text-lg sm:text-2xl md:text-3xl font-black card-rank-text';
  if (fontSize === 'extra-large') return 'text-[22.55px] sm:text-[28.6px] md:text-[35.2px] font-black card-rank-text';
  if (fontSize === 'medium') return 'text-[21.175px] sm:text-[27.5px] md:text-[34.1px] font-black card-rank-text';
  return 'text-[19.8px] sm:text-[26.4px] md:text-[33px] font-black card-rank-text';
};

const getSuitClass = (size: string, isSouthHand: boolean, fontSize: SouthHandFontSize) => {
  if (size === 'xs') return 'text-[8px] font-bold card-suit-symbol';
  if (size === 'sm') return `${SM_TYPO.suit[fontSize] ?? SM_TYPO.suit.regular} font-bold card-suit-symbol`;
  if (size === 'lg') return `${LG_TYPO.suit} font-bold card-suit-symbol`;
  if (!isSouthHand) return 'text-base sm:text-xl md:text-2xl font-bold card-suit-symbol';
  if (fontSize === 'extra-large') return 'text-[19.8px] sm:text-[24.75px] md:text-[28.6px] font-bold card-suit-symbol';
  if (fontSize === 'medium') return 'text-[18.7px] sm:text-[23.375px] md:text-[27.5px] font-bold card-suit-symbol';
  return 'text-[17.6px] sm:text-[22px] md:text-[26.4px] font-bold card-suit-symbol';
};

const getJokerStarClass = (size: string, isSouthHand: boolean, fontSize: SouthHandFontSize) => {
  if (size === 'xs') return 'text-[10px] card-suit-symbol';
  if (size === 'sm') return `${SM_TYPO.star[fontSize] ?? SM_TYPO.star.regular} card-suit-symbol`;
  if (size === 'lg') return `${LG_TYPO.star} card-suit-symbol`;
  if (!isSouthHand) return 'text-lg sm:text-2xl md:text-3xl card-suit-symbol';
  if (fontSize === 'extra-large') return 'text-[23.1px] sm:text-[28.6px] md:text-[35.2px] card-suit-symbol';
  if (fontSize === 'medium') return 'text-[21.45px] sm:text-[27.5px] md:text-[34.1px] card-suit-symbol';
  return 'text-[19.8px] sm:text-[26.4px] md:text-[33px] card-suit-symbol';
};

const getCenterSymbolClass = (size: string, isSouthHand: boolean, fontSize: SouthHandFontSize) => {
  if (size === 'xs') return 'text-base';
  if (size === 'sm') return SM_TYPO.center[fontSize] ?? SM_TYPO.center.regular;
  if (size === 'lg') return LG_TYPO.center;
  if (!isSouthHand) return 'text-5xl sm:text-6xl md:text-7xl';
  if (fontSize === 'extra-large') return 'text-[59.4px] sm:text-[72.6px] md:text-[85.8px]';
  if (fontSize === 'medium') return 'text-[56.1px] sm:text-[69.3px] md:text-[82.5px]';
  return 'text-[52.8px] sm:text-[66px] md:text-[79.2px]';
};

export const CardView: React.FC<CardViewProps> = React.memo(({
  card,
  isPlayable = true,
  isSelected = false,
  isFaceDown = false,
  onClick,
  className = '',
  size = 'md',
  isGold = false,
  hasShadow = true,
  isHearts: propIsHearts,
  gameType: propGameType,
  isSouthHand = false,
  southHandFontSize: propSouthHandFontSize,
  showHints: propShowHints,
}) => {
  const isBlackClubs = useClubColor();
  const hookSouthHandFontSize = useSouthHandFontSize();
  const hookShowHints = useCardHints();
  const effectiveSouthHandFontSize = propSouthHandFontSize || hookSouthHandFontSize;
  const effectiveShowHints = propShowHints !== undefined ? propShowHints : hookShowHints;
  const isDimmed = !isPlayable && effectiveShowHints && !isSelected && !isGold;
  const storeEffectiveGameType = useArenaStore((s) => s.effectiveGameState?.gameType || s.gameConfig?.gameType);
  if (!card) return null;

  const isBig = Boolean(card?.isBigJoker || card?.rank === 'BJ');
  const isLittle = Boolean(card?.isLittleJoker || card?.rank === 'LJ');
  const isJoker = card?.suit === 'JOKER' || card?.suit === 'Joker' || isBig || isLittle;
  const isRed = getSuitColor(card?.suit) === 'red';
  const isClub = card?.suit === 'C' || card?.suit === 'Clubs';
  const symbol = getSuitSymbol(card?.suit);
  const isSpade = card?.suit === 'S' || card?.suit === 'Spades';
  const isQueen = card?.rank === 'Q' || (card?.rank as string) === 'Queen' || card?.id === 'S_Q' || card?.id === 'QS';
  const isQueenOfSpades = isSpade && isQueen;

  // The custom African Queen of Spades portrait is strictly exclusive to the Hearts game.
  const activeGameType = (propGameType || storeEffectiveGameType || '').toUpperCase();
  const isHeartsGame =
    propIsHearts !== undefined
      ? propIsHearts
      : activeGameType === 'HEARTS' || activeGameType === '100_PTS' || activeGameType === '50_PTS' || activeGameType === 'HEART';
  const showHeartsQueenPortrait = isQueenOfSpades && isHeartsGame;

  const suitColorClass = isRed
    ? 'text-red-600'
    : isJoker
    ? (isBig ? 'text-amber-600' : 'text-slate-700')
    : isClub
    ? (isBlackClubs ? 'text-slate-950' : 'text-emerald-600')
    : 'text-slate-950';

  const sizeClasses = {
    xs: 'w-[36px] h-[52px] text-[9px] rounded-md',
    sm: 'w-[80px] h-[116px] sm:w-[86px] sm:h-[124px] text-xs rounded-xl',
    md: 'w-[85px] h-[122px] sm:w-[94px] sm:h-[135px] md:w-[103px] md:h-[148px] rounded-2xl',
    lg: 'w-26 h-36 sm:w-30 sm:h-42 text-sm sm:text-base rounded-2xl'
  }[size];

  const jokerFinSizes = {
    xs: {
      fin: 'w-6 h-5',
      badge: 'text-[4px] px-0.5 py-0 tracking-tight'
    },
    sm: {
      fin: 'w-12 h-10 sm:w-13 sm:h-11',
      badge: 'text-[6.5px] sm:text-[7.5px] px-1 sm:px-1.5 py-0.2 tracking-tight'
    },
    md: {
      fin: 'w-14 h-11 sm:w-15 sm:h-12 md:w-17 md:h-13',
      badge: 'text-[7px] sm:text-[7.5px] md:text-[8px] px-1 sm:px-1.5 py-0.5 tracking-tight'
    },
    lg: {
      fin: 'w-18 h-14 sm:w-22 sm:h-17',
      badge: 'text-[8.5px] sm:text-[9.5px] px-1.5 sm:px-2 py-0.5 tracking-tight'
    }
  }[size];

  if (isFaceDown) {
    return <CardBack size={size} className={className} />;
  }

  return (
    <div
      onClick={onClick}
      className={`
        ${sizeClasses}
        card-view card-crisp relative overflow-hidden bg-gradient-to-b from-white via-[#fafbfe] to-[#f1f5f9] opacity-100 border ring-1
        ${isSelected
          ? 'border-amber-400 ring-amber-400/80'
          : isGold
          ? 'border-amber-400/90 ring-amber-400/40'
          : isDimmed
          ? 'border-slate-900/60 ring-slate-950/40'
          : isPlayable
          ? 'border-slate-300/90 ring-slate-900/10 hover:border-amber-400 hover:ring-amber-400/50'
          : 'border-slate-300/70 ring-slate-900/5 hover:border-amber-300/60'
        }
        flex flex-col justify-between ${size === 'xs' ? 'p-0.5 sm:p-1' : isSouthHand ? 'p-1 sm:p-2' : 'p-1.5 sm:p-2.5'}
        select-none transition-[border-color] duration-200
        ${onClick ? 'cursor-pointer' : 'pointer-events-none'}
        ${className}
      `}
      style={{
        boxShadow: !hasShadow
          ? 'none'
          : isSelected
          ? '0 18px 40px -4px rgba(245, 158, 11, 0.7)'
          : isGold
          ? '0 10px 22px -3px rgba(245, 158, 11, 0.35)'
          : isPlayable
          ? '0 10px 25px -4px rgba(0, 0, 0, 0.45)'
          : '0 4px 12px -2px rgba(0, 0, 0, 0.25)',
      }}
    >
      {/* Playability Dimming Scrim: dims unplayable cards without altering 100% solid opacity */}
      {isDimmed && (
        <div
          data-testid="card-dimmed-scrim"
          className="absolute -inset-px bg-slate-950/35 rounded-[inherit] pointer-events-none z-20 transition-opacity duration-200"
        />
      )}
      {/* Top Left Rank & Suit */}
      <div className={`flex flex-col items-start leading-none z-10 ${suitColorClass}`}>
        {isJoker ? (
          <span className={`${getJokerStarClass(size, isSouthHand, effectiveSouthHandFontSize)} font-black leading-none select-none`}>
            ★
          </span>
        ) : (
          <>
            <span className={`${getRankClass(size, isSouthHand, effectiveSouthHandFontSize)} tracking-tight leading-none`}>
              {card.rank}
            </span>
            <span className={`${getSuitClass(size, isSouthHand, effectiveSouthHandFontSize)} leading-none mt-0.5`}>
              {symbol}
            </span>
          </>
        )}
      </div>

      {/* Center Natural Graphic (Shark Fin for Jokers, Natural Suit symbol for standard cards) */}
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none select-none">
        {isJoker ? (
          <div className="flex flex-col items-center justify-center">
            <div className="relative flex items-center justify-center">
              <svg
                viewBox="0 0 36 28"
                className={`${jokerFinSizes.fin} ${
                  isBig
                    ? 'drop-shadow-[0_4px_10px_rgba(245,158,11,0.45)]'
                    : 'drop-shadow-[0_4px_10px_rgba(30,41,59,0.45)]'
                }`}
              >
                <defs>
                  <linearGradient id={isBig ? 'centerBigJokerFin' : 'centerLilJokerFin'} x1="0%" y1="0%" x2="100%" y2="100%">
                    {isBig ? (
                      <>
                        <stop offset="0%" stopColor="#fef08a" />
                        <stop offset="45%" stopColor="#f59e0b" />
                        <stop offset="100%" stopColor="#b45309" />
                      </>
                    ) : (
                      <>
                        <stop offset="0%" stopColor="#ffffff" />
                        <stop offset="25%" stopColor="#cbd5e1" />
                        <stop offset="60%" stopColor="#475569" />
                        <stop offset="100%" stopColor="#0f172a" />
                      </>
                    )}
                  </linearGradient>
                  <radialGradient id={isBig ? 'bigJokerAura' : 'lilJokerAura'} cx="50%" cy="65%" r="55%">
                    <stop offset="0%" stopColor={isBig ? '#fde68a' : '#e2e8f0'} stopOpacity={isBig ? 0.4 : 0.5} />
                    <stop offset="60%" stopColor={isBig ? '#f59e0b' : '#94a3b8'} stopOpacity={isBig ? 0.12 : 0.2} />
                    <stop offset="100%" stopColor={isBig ? '#d97706' : '#334155'} stopOpacity="0" />
                  </radialGradient>
                </defs>

                {/* Ambient Surface Water Glow */}
                <ellipse cx="18" cy="20" rx="16" ry="7" fill={`url(#${isBig ? 'bigJokerAura' : 'lilJokerAura'})`} />

                {/* Sleek, Swept-Back Shark Fin (Smooth rounded apex, concave trailing falcate curve, signature rear notch) */}
                <path
                  d="M 4,22 C 7,21.5 12,17.5 16.5,13 C 19,10.5 21.5,8.5 23.5,8.2 C 25,8.2 26.2,9.2 25.8,11 C 24.8,14 22.2,17 22.5,19.2 C 23.5,20 27.5,20.6 30,20.6 C 28.5,21.8 26,22 24,22 L 4,22 Z"
                  fill={`url(#${isBig ? 'centerBigJokerFin' : 'centerLilJokerFin'})`}
                  stroke={isBig ? '#fef08a' : '#cbd5e1'}
                  strokeWidth="0.8"
                  strokeLinejoin="round"
                />

                {/* Dynamic Leading Edge Ridge Highlight */}
                <path
                  d="M 7,20.5 C 11,18 15,14.5 18.5,11 C 20.5,9.5 22,9 23,8.8"
                  stroke="#ffffff"
                  strokeWidth="0.9"
                  strokeLinecap="round"
                  opacity={isBig ? 0.65 : 0.85}
                  fill="none"
                />

                {/* Primary Rhythmic Wavy Water Line */}
                <path
                  d="M 1,21.5 C 3,19.5 5,23.5 7.5,21.5 C 10,19.5 12,23.5 14.5,21.5 C 17,19.5 19.5,23.5 22,21.5 C 24.5,19.5 27,23.5 29.5,21.5 C 32,19.5 34,23.5 35.5,21.5"
                  stroke={isBig ? '#f59e0b' : '#334155'}
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  fill="none"
                />

                {/* Secondary Offset Wavy Wake Ripple */}
                <path
                  d="M 2.5,24 C 5,22.2 7,25.8 9.5,24 C 12,22.2 14,25.8 16.5,24 C 19,22.2 21,25.8 23.5,24 C 26,22.2 28,25.8 30.5,24 C 32.5,22.5 34,25 35,24"
                  stroke={isBig ? '#fbbf24' : '#64748b'}
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  opacity="0.85"
                  fill="none"
                />

                {/* Tertiary Subtle Ocean Ripple */}
                <path
                  d="M 5.5,26.5 C 8,25.2 10.5,27.8 13,26.5 C 15.5,25.2 18,27.8 20.5,26.5 C 23,25.2 25.5,27.8 28,26.5 C 30,25.4 32,27.2 33.5,26.5"
                  stroke={isBig ? '#d97706' : '#94a3b8'}
                  strokeWidth="0.9"
                  strokeLinecap="round"
                  opacity="0.65"
                  fill="none"
                />
              </svg>
            </div>

            {/* Center Title Badge */}
            <div className="mt-0.5 sm:mt-1 flex items-center justify-center max-w-[calc(100%-10px)] sm:max-w-[calc(100%-14px)] px-1 mx-auto">
              <span
                className={`
                  font-sans font-black uppercase tracking-tight shadow-xs rounded-sm sm:rounded-md border whitespace-nowrap
                  ${jokerFinSizes.badge}
                  ${
                    isBig
                      ? 'text-amber-950 bg-gradient-to-r from-amber-500/15 via-amber-500/25 to-amber-500/15 border-amber-500/40'
                      : 'text-slate-900 bg-gradient-to-r from-slate-400/20 via-slate-200/40 to-slate-400/20 border-slate-400/50'
                  }
                `}
              >
                {isBig ? 'BIG JOKER' : 'LITTLE JOKER'}
              </span>
            </div>
          </div>
        ) : showHeartsQueenPortrait ? (
          <div className="flex flex-col items-center justify-center">
            <QueenOfSpadesPortrait size={size} />
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <span className={`${getCenterSymbolClass(size, isSouthHand, effectiveSouthHandFontSize)} card-suit-symbol font-bold leading-none select-none ${suitColorClass}`}>
              {symbol}
            </span>
          </div>
        )}
      </div>

      {/* Bottom Right Inverted Rank & Suit */}
      <div className={`flex flex-col items-end leading-none rotate-180 z-10 ${suitColorClass}`}>
        {isJoker ? (
          <span className={`${getJokerStarClass(size, isSouthHand, effectiveSouthHandFontSize)} font-black leading-none select-none`}>
            ★
          </span>
        ) : (
          <>
            <span className={`${getRankClass(size, isSouthHand, effectiveSouthHandFontSize)} tracking-tight leading-none`}>
              {card.rank}
            </span>
            <span className={`${getSuitClass(size, isSouthHand, effectiveSouthHandFontSize)} leading-none mt-0.5`}>
              {symbol}
            </span>
          </>
        )}
      </div>
    </div>
  );
});
